import { supabase, isConfigured } from './supabase';
import type {
  Contact,
  ContactType,
  ContactStatus,
  IcpFit,
  LeadSegment,
  ChannelPreference,
} from '../types';

export interface CreateContactInput {
  team_id: string;
  owner_user_id?: string | null;
  type: ContactType;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  title?: string | null;
  company?: string | null;
  company_url?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  country?: string | null;
  external_id?: string | null;
  icp_fit?: IcpFit | null;
  segment?: LeadSegment | null;
  channel_preference?: ChannelPreference | null;
  hook?: string | null;
  hook_source_url?: string | null;
  hook_date?: string | null;
  enriched_on?: string | null;
  research_notes?: string | null;
  email_subject?: string | null;
  email_body?: string | null;
  linkedin_note?: string | null;
  drafted_on?: string | null;
  last_touch_on?: string | null;
  next_step?: string | null;
  sent_on?: string | null;
  replied_on?: string | null;
  queue_skipped_on?: string | null;
  email_source_note?: string | null;
  source?: string | null;
  status?: ContactStatus;
  next_follow_up?: string | null;
  notes?: string | null;
}

export interface UpdateContactInput extends Partial<Omit<CreateContactInput, 'team_id'>> {
  id: string;
}

export async function getContacts(
  teamId: string,
  options?: { allTeams?: boolean }
): Promise<Contact[]> {
  if (!isConfigured) return [];

  let query = supabase
    .from('sales_contacts')
    .select('*')
    .order('created_at', { ascending: false });

  if (!options?.allTeams) {
    query = query.eq('team_id', teamId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[contacts] fetch failed:', error);
    return [];
  }

  return data as Contact[];
}

export async function getContact(id: string): Promise<Contact | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_contacts')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return null;
  return data as Contact;
}

export async function createContact(input: CreateContactInput): Promise<Contact | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_contacts')
    .insert(input)
    .select()
    .single();

  if (error) {
    console.error('[contacts] create failed:', error);
    return null;
  }

  return data as Contact;
}

export async function updateContact(input: UpdateContactInput): Promise<Contact | null> {
  if (!isConfigured) return null;

  const { id, ...updates } = input;
  const { data, error } = await supabase
    .from('sales_contacts')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[contacts] update failed:', error);
    return null;
  }

  return data as Contact;
}

export async function deleteContact(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_contacts')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[contacts] delete failed:', error);
    return false;
  }

  return true;
}

export async function bulkCreateContacts(
  contacts: CreateContactInput[]
): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> {
  if (!isConfigured) {
    return { created: 0, updated: 0, skipped: 0, errors: ['Not configured'] };
  }

  const results = { created: 0, updated: 0, skipped: 0, errors: [] as string[] };

  for (const contact of contacts) {
    const payload = {
      ...contact,
      full_name:
        contact.full_name ||
        [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
        null,
    };

    let existing: { id: string } | null = null;
    if (payload.external_id) {
      const { data } = await supabase
        .from('sales_contacts')
        .select('id')
        .eq('team_id', payload.team_id)
        .eq('external_id', payload.external_id)
        .maybeSingle();
      existing = data;
    }
    if (!existing && payload.email) {
      let query = supabase
        .from('sales_contacts')
        .select('id')
        .eq('team_id', payload.team_id)
        .eq('email', payload.email);
      if (payload.company) query = query.eq('company', payload.company);
      const { data } = await query.maybeSingle();
      existing = data;
    }

    if (existing) {
      const { error } = await supabase
        .from('sales_contacts')
        .update(payload)
        .eq('id', existing.id);
      if (error) {
        results.errors.push(`${payload.email || payload.external_id || 'Unknown'}: ${error.message}`);
      } else {
        results.updated++;
      }
      continue;
    }

    const { error } = await supabase.from('sales_contacts').insert(payload);
    if (error) {
      if (error.code === '23505') {
        results.skipped++;
      } else {
        results.errors.push(`${payload.email || payload.external_id || 'Unknown'}: ${error.message}`);
      }
    } else {
      results.created++;
    }
  }

  return results;
}

export async function linkContactToRehubUser(
  contactId: string,
  rehubUserId: string,
  rehubTeamId?: string | null
): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase.rpc('link_contact_to_rehub_user', {
    p_contact_id: contactId,
    p_rehub_user_id: rehubUserId,
    p_rehub_team_id: rehubTeamId || null,
  });

  if (error) {
    console.error('[contacts] linkContactToRehubUser failed:', error);
    return false;
  }

  return true;
}

export async function convertContactToCustomer(
  contactId: string,
  email: string,
  teamName?: string
): Promise<{ success: boolean; userId?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  const { data: existing } = await supabase
    .from('profiles')
    .select('id')
    .eq('email', email.toLowerCase())
    .maybeSingle();

  if (existing) {
    const linked = await linkContactToRehubUser(contactId, existing.id);
    if (linked) {
      return { success: true, userId: existing.id };
    }
    return { success: false, error: 'User exists but linking failed' };
  }

  const { data: userData, error: createError } = await supabase.auth.admin.createUser({
    email: email.toLowerCase(),
    email_confirm: false,
    user_metadata: { source: 'sales_crm_convert' },
  });

  if (createError || !userData.user) {
    return {
      success: false,
      error: createError?.message || 'Failed to create user',
    };
  }

  let rehubTeamId: string | null = null;
  if (teamName) {
    const { data: newTeam } = await supabase
      .from('teams')
      .insert({ name: teamName, owner_id: userData.user.id })
      .select('id')
      .single();
    if (newTeam) {
      rehubTeamId = newTeam.id;
      await supabase.from('team_members').insert({
        team_id: newTeam.id,
        user_id: userData.user.id,
        role: 'owner',
      });
    }
  }

  const linked = await linkContactToRehubUser(contactId, userData.user.id, rehubTeamId);
  if (!linked) {
    return { success: false, error: 'User created but linking failed' };
  }

  return { success: true, userId: userData.user.id };
}

export async function setSalesOwner(
  contactId: string,
  salesOwnerUserId: string
): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_contacts')
    .update({ sales_owner_user_id: salesOwnerUserId })
    .eq('id', contactId);

  if (error) {
    console.error('[contacts] setSalesOwner failed:', error);
    return false;
  }

  await supabase
    .from('sales_customer_attribution')
    .update({ sales_user_id: salesOwnerUserId })
    .eq('contact_id', contactId);

  return true;
}
